// SEQ-193 review: which fetched project rows a month-on-month table may show
// right now. Pure, so a deno test can prove the rule without a React harness.
//
// THE RULE. Results are stored together with the request key they were
// fetched for. They are only shown while that key equals the CURRENT
// request key. The moment the inputs change (a new range, metric, project
// list or status filter) the stored results stop matching, so in the same
// render the table reports "loading" and shows none of the old figures:
// no flash of N/A while the debounce waits, and never the previous range's
// values under the new range's headers. No effect has to run first.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";

export interface MonthOnMonthFetched {
  /** The request key these results were fetched for; null before the first fetch lands. */
  key: string | null;
  rowsByProject: Record<string, PnlAnalyticsRow[]>;
  errorByProject: Record<string, string>;
}

export interface MonthOnMonthProjectView {
  rowsByProject: Record<string, PnlAnalyticsRow[]>;
  errorByProject: Record<string, string>;
  loading: boolean;
}

/**
 * Own-property lookup. A widget's project ids are user-supplied strings, and
 * a plain `record[id]` or `id in record` for "constructor", "toString" or
 * "__proto__" reaches Object.prototype and, in render, threw and blanked the
 * whole page. Only keys this record actually owns count.
 */
export function ownEntry<T>(record: Record<string, T>, id: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(record, id) ? record[id] : undefined;
}

export const EMPTY_FETCHED: MonthOnMonthFetched = { key: null, rowsByProject: {}, errorByProject: {} };

const NONE: Record<string, never> = {};

export function viewForRequest(fetched: MonthOnMonthFetched, currentKey: string, active: boolean): MonthOnMonthProjectView {
  if (!active) return { rowsByProject: NONE, errorByProject: NONE, loading: false };
  if (fetched.key !== currentKey) return { rowsByProject: NONE, errorByProject: NONE, loading: true };
  return { rowsByProject: fetched.rowsByProject, errorByProject: fetched.errorByProject, loading: false };
}
